import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./AdminRegionalGovernance.css";

type OperationalState="ACTION_REQUIRED"|"FOLLOW_UP"|"STABLE"|string;
type SupportStatus="MONITORING"|"SUPPORTING"|"RESOLVED";

type SupportCase={
  caseId:string;
  status:SupportStatus;
  note?:string|null;
  updatedAt:string;
  updatedBy?:string|null;
};

type SchoolGovernance={
  schoolId:string;
  schoolName:string;
  schoolCode?:string|null;
  academicYearId?:string|null;
  yearCode?:string|null;
  studentCount:number;
  staffCount:number;
  classCount:number;
  assessmentAssigned:number;
  assessmentCompleted:number;
  assessmentPending:number;
  assessmentCompletionRate:number;
  badgeIssued:number;
  badgeClaimed:number;
  badgeClaimPending:number;
  badgeClaimRate:number;
  studentsWithoutClass:number;
  classesWithoutStaff:number;
  badgeDeliveryIssues:number;
  credentialFailures:number;
  notificationFailures:number;
  reportReviewPending:number;
  latestReportStatus?:string|null;
  latestReportSubmittedAt?:string|null;
  latestReportReviewedAt?:string|null;
  missingActiveYear:boolean;
  signalCount:number;
  operationalState:OperationalState;
  supportCase?:SupportCase|null;
  signals:Record<string,number>;
};

type Governance={
  root:{organizationId:string;name:string;code?:string|null;type:string};
  summary:{
    schoolCount:number;
    actionRequiredSchools:number;
    followUpSchools:number;
    stableSchools:number;
    monitoringSchools:number;
    supportingSchools:number;
    reportReviewPending:number;
    totalSignals:number;
    assessmentPending:number;
    badgeClaimPending:number;
  };
  signalTotals:{
    missingActiveYear:number;
    studentsWithoutClass:number;
    classesWithoutStaff:number;
    assessmentPending:number;
    badgeClaimPending:number;
    badgeDeliveryIssues:number;
    credentialFailures:number;
    notificationFailures:number;
    reportsReturned:number;
    reportReviewPending:number;
  };
  schools:SchoolGovernance[];
};

const SIGNAL_LABEL:Record<string,string>={
  missingActiveYear:"활성 학년도 없음",
  studentsWithoutClass:"학급 미배정 학생",
  classesWithoutStaff:"담당교사 미배정 학급",
  assessmentPending:"검사 미완료",
  badgeClaimPending:"배지 미수령",
  badgeDeliveryIssues:"배지 전달 문제",
  credentialFailures:"Credential 실패",
  notificationFailures:"외부 알림 실패",
  reportReturned:"보고서 보완 요청",
  reportReviewPending:"보고서 검토 대기"
};

function fmt(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"
  });
}

function operationalLabel(value:string){
  if(value==="ACTION_REQUIRED")return "지원 필요";
  if(value==="FOLLOW_UP")return "후속 확인";
  if(value==="STABLE")return "안정";
  return value;
}

function supportLabel(value?:string|null){
  if(value==="MONITORING")return "모니터링";
  if(value==="SUPPORTING")return "지원 중";
  if(value==="RESOLVED")return "해결";
  return "미지정";
}

function reportLabel(value?:string|null){
  if(!value)return "제출 없음";
  if(value==="SUBMITTED")return "검토 대기";
  if(value==="RETURNED")return "보완 요청";
  if(value==="APPROVED")return "승인";
  return value;
}

export function AdminRegionalGovernance({
  client,
  rootOrganizationId,
  canManagePlatformAccess,
  onOpenSection
}:{
  client:PlatformClient;
  rootOrganizationId:string;
  canManagePlatformAccess:boolean;
  onOpenSection(section:"SCHOOLS"|"SUBMISSIONS"|"DISTRIBUTION"):void;
}){
  const [data,setData]=useState<Governance|null>(null);
  const [selectedId,setSelectedId]=useState("");
  const [stateFilter,setStateFilter]=useState<"ALL"|"ACTION_REQUIRED"|"FOLLOW_UP"|"STABLE">("ALL");
  const [supportFilter,setSupportFilter]=useState<"ALL"|"ACTIVE_SUPPORT"|"NONE">("ALL");
  const [query,setQuery]=useState("");
  const [note,setNote]=useState("");
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("admin_regional_governance",{p_root_organization_id:rootOrganizationId}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as Governance;
      setData(next);
      setSelectedId((current)=>
        next.schools.some((item)=>item.schoolId===current)
          ? current
          : next.schools[0]?.schoolId || ""
      );
    });
    return()=>{active=false;};
  },[client,rootOrganizationId,refresh]);

  const selected=useMemo(
    ()=>data?.schools.find((item)=>item.schoolId===selectedId) || null,
    [data,selectedId]
  );

  useEffect(()=>{
    setNote(selected?.supportCase?.note || "");
  },[selected?.schoolId,selected?.supportCase?.updatedAt]);

  const visibleSchools=useMemo(()=>{
    const keyword=query.trim().toLowerCase();
    return (data?.schools || []).filter((item)=>{
      if(stateFilter!=="ALL" && item.operationalState!==stateFilter)return false;
      if(supportFilter==="ACTIVE_SUPPORT" && !["MONITORING","SUPPORTING"].includes(item.supportCase?.status || ""))return false;
      if(supportFilter==="NONE" && item.supportCase && item.supportCase.status!=="RESOLVED")return false;
      return !keyword || [item.schoolName,item.schoolCode || "",item.yearCode || ""]
        .some((value)=>value.toLowerCase().includes(keyword));
    });
  },[data,query,stateFilter,supportFilter]);

  useEffect(()=>{
    if(visibleSchools.length && !visibleSchools.some((item)=>item.schoolId===selectedId)){
      setSelectedId(visibleSchools[0].schoolId);
    }
  },[visibleSchools,selectedId]);

  async function setSupport(status:SupportStatus){
    if(!selected)return;
    setBusy(status);
    setError("");
    setMessage("");
    const {error}=await client.rpc("admin_set_regional_school_support",{
      p_root_organization_id:rootOrganizationId,
      p_school_id:selected.schoolId,
      p_status:status,
      p_note:note.trim() || null
    });
    setBusy("");
    if(error){setError(error.message);return;}
    setMessage(
      status==="SUPPORTING" ? selected.schoolName+"을 지원 중으로 표시했습니다."
      : status==="MONITORING" ? selected.schoolName+"을 모니터링 대상으로 표시했습니다."
      : selected.schoolName+" 지원 건을 해결 처리했습니다."
    );
    setRefresh((value)=>value+1);
  }

  if(error)return <p className="error">{error}</p>;
  if(!data)return <p className="muted">지역 운영관제를 불러오는 중…</p>;

  const s=data.summary;
  const signalEntries=Object.entries(data.signalTotals)
    .filter(([,value])=>value>0)
    .sort((a,b)=>b[1]-a[1]);

  return (
    <section className="regional-governance">
      {message && <p className="teacher-success">{message}</p>}

      <div className="regional-governance-summary">
        <GovernanceKpi label="지원 필요 학교" value={s.actionRequiredSchools} tone={s.actionRequiredSchools>0?"danger":"normal"} />
        <GovernanceKpi label="후속 확인 학교" value={s.followUpSchools} tone={s.followUpSchools>0?"warn":"normal"} />
        <GovernanceKpi label="지원 중" value={s.supportingSchools} />
        <GovernanceKpi label="모니터링" value={s.monitoringSchools} />
        <GovernanceKpi label="보고서 검토대기" value={s.reportReviewPending} />
        <GovernanceKpi label="전체 지원 신호" value={s.totalSignals} />
      </div>

      <div className="regional-governance-topgrid">
        <section className="regional-governance-card">
          <div className="regional-section-head">
            <div>
              <p className="eyebrow">SUPPORT SIGNALS</p>
              <h3>현재 지원 신호</h3>
            </div>
            <button className="text-button" onClick={()=>onOpenSection("SCHOOLS")}>학교 비교 →</button>
          </div>
          <div className="regional-signal-grid">
            {signalEntries.map(([key,value])=>(
              <div key={key}>
                <span>{SIGNAL_LABEL[key] || key}</span>
                <strong>{value}</strong>
              </div>
            ))}
            {!signalEntries.length && (
              <div className="regional-signal-clear">
                <strong>지원 신호 없음</strong>
                <span>현재 집계 규칙 기준 특이사항이 없습니다.</span>
              </div>
            )}
          </div>
        </section>

        <section className="regional-governance-card">
          <div className="regional-section-head">
            <div>
              <p className="eyebrow">REGIONAL INBOX</p>
              <h3>상위기관 처리항목</h3>
            </div>
          </div>
          <div className="regional-inbox-actions">
            <button onClick={()=>onOpenSection("SUBMISSIONS")}>
              <span>공식 보고서 검토</span>
              <strong>{s.reportReviewPending}</strong>
              <small>제출된 학교 보고서 승인·보완</small>
            </button>
            <button onClick={()=>onOpenSection("DISTRIBUTION")}>
              <span>검사 미완료</span>
              <strong>{s.assessmentPending}</strong>
              <small>학교별 검사 배포·참여 추적</small>
            </button>
            <button onClick={()=>setStateFilter("FOLLOW_UP")}>
              <span>배지 미수령</span>
              <strong>{s.badgeClaimPending}</strong>
              <small>학생 Wallet 수령 후속 확인</small>
            </button>
          </div>
        </section>
      </div>

      <div className="regional-school-toolbar">
        <input value={query} onChange={(event)=>setQuery(event.target.value)} placeholder="학교명·학교코드·학년도 검색" />
        <select value={stateFilter} onChange={(event)=>setStateFilter(event.target.value as typeof stateFilter)}>
          <option value="ALL">전체 운영상태</option>
          <option value="ACTION_REQUIRED">지원 필요</option>
          <option value="FOLLOW_UP">후속 확인</option>
          <option value="STABLE">안정</option>
        </select>
        <select value={supportFilter} onChange={(event)=>setSupportFilter(event.target.value as typeof supportFilter)}>
          <option value="ALL">전체 지원상태</option>
          <option value="ACTIVE_SUPPORT">모니터링·지원중</option>
          <option value="NONE">미지정·해결</option>
        </select>
      </div>

      <div className="regional-school-layout">
        <aside className="regional-school-list">
          <div className="panel-title"><strong>학교 지원 Inbox</strong><span>{visibleSchools.length}개교</span></div>
          {visibleSchools.map((school)=>(
            <button key={school.schoolId} className={selectedId===school.schoolId?"active":""} onClick={()=>setSelectedId(school.schoolId)}>
              <div>
                <span className={"regional-state "+school.operationalState.toLowerCase()}>{operationalLabel(school.operationalState)}</span>
                <span className={"regional-support "+(school.supportCase?.status || "none").toLowerCase()}>{supportLabel(school.supportCase?.status)}</span>
              </div>
              <strong>{school.schoolName}</strong>
              <p>{school.yearCode || "활성 학년도 없음"} · 학생 {school.studentCount}명</p>
              <small>지원 신호 {school.signalCount}개</small>
            </button>
          ))}
          {!visibleSchools.length && <p className="case-empty">조건에 맞는 학교가 없습니다.</p>}
        </aside>

        <main className="regional-school-detail">
          {selected ? (
            <>
              <header className="regional-school-detail-head">
                <div>
                  <div className="regional-detail-tags">
                    <span className={"regional-state "+selected.operationalState.toLowerCase()}>{operationalLabel(selected.operationalState)}</span>
                    <span>{supportLabel(selected.supportCase?.status)}</span>
                  </div>
                  <h2>{selected.schoolName}</h2>
                  <p>{selected.schoolCode || "학교코드 없음"} · {selected.yearCode || "활성 학년도 없음"}</p>
                </div>
                {canManagePlatformAccess && (
                  <button className="secondary" onClick={()=>window.location.assign("/access?organization="+encodeURIComponent(selected.schoolId))}>
                    사용자·권한
                  </button>
                )}
              </header>

              <div className="regional-school-metrics">
                <Metric label="학생" value={selected.studentCount} />
                <Metric label="교직원" value={selected.staffCount} />
                <Metric label="학급" value={selected.classCount} />
                <Metric label="검사 완료율" value={selected.assessmentCompletionRate+"%"} />
                <Metric label="배지 수령률" value={selected.badgeClaimRate+"%"} />
                <Metric label="지원 신호" value={selected.signalCount} tone={selected.signalCount>0?"warn":"normal"} />
              </div>

              <section className="regional-school-card">
                <div className="regional-section-head">
                  <div><p className="eyebrow">OPERATION SIGNALS</p><h3>학교 운영 신호</h3></div>
                  <span>{selected.signalCount} signals</span>
                </div>
                <div className="regional-school-signals">
                  {Object.entries(selected.signals).map(([key,value])=>(
                    <div key={key}>
                      <span>{SIGNAL_LABEL[key] || key}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                  {!Object.keys(selected.signals).length && <div className="regional-signal-clear"><strong>특이사항 없음</strong><span>현재 운영 신호가 없습니다.</span></div>}
                </div>
              </section>

              <div className="regional-school-two">
                <section className="regional-school-card">
                  <div className="regional-section-head"><div><p className="eyebrow">ASSESSMENT & BADGE</p><h3>학생 참여</h3></div></div>
                  <div className="regional-line-list">
                    <Line label="검사 배정" value={selected.assessmentAssigned} />
                    <Line label="검사 완료" value={selected.assessmentCompleted} />
                    <Line label="검사 미완료" value={selected.assessmentPending} attention={selected.assessmentPending>0} />
                    <Line label="배지 발급" value={selected.badgeIssued} />
                    <Line label="배지 수령" value={selected.badgeClaimed} />
                    <Line label="배지 미수령" value={selected.badgeClaimPending} attention={selected.badgeClaimPending>0} />
                  </div>
                </section>

                <section className="regional-school-card">
                  <div className="regional-section-head"><div><p className="eyebrow">OFFICIAL REPORT</p><h3>학교 보고서</h3></div></div>
                  <div className="regional-report-status">
                    <span>{reportLabel(selected.latestReportStatus)}</span>
                    <strong>{selected.latestReportStatus || "NO SUBMISSION"}</strong>
                    <small>제출 {fmt(selected.latestReportSubmittedAt)} · 검토 {fmt(selected.latestReportReviewedAt)}</small>
                  </div>
                  {selected.reportReviewPending>0 && <button className="secondary" onClick={()=>onOpenSection("SUBMISSIONS")}>보고서 검토하기</button>}
                </section>
              </div>

              <section className="regional-school-card support-manager">
                <div className="regional-section-head">
                  <div>
                    <p className="eyebrow">REGIONAL SUPPORT CASE</p>
                    <h3>학교 지원관리</h3>
                  </div>
                  {selected.supportCase && <span>최근 변경 {fmt(selected.supportCase.updatedAt)} · {selected.supportCase.updatedBy || "-"}</span>}
                </div>
                <textarea
                  rows={4}
                  value={note}
                  onChange={(event)=>setNote(event.target.value)}
                  placeholder="학교 확인 내용, 지원 계획, 후속 조치 메모"
                />
                <div className="regional-support-actions">
                  <button className="secondary" disabled={busy!==""} onClick={()=>setSupport("MONITORING")}>
                    {busy==="MONITORING"?"저장 중…":"모니터링"}
                  </button>
                  <button className="secondary" disabled={busy!==""} onClick={()=>setSupport("SUPPORTING")}>
                    {busy==="SUPPORTING"?"저장 중…":"지원 중"}
                  </button>
                  <button className="primary" disabled={busy!==""} onClick={()=>setSupport("RESOLVED")}>
                    {busy==="RESOLVED"?"저장 중…":"해결 처리"}
                  </button>
                </div>
              </section>
            </>
          ):<p className="case-empty">학교를 선택하세요.</p>}
        </main>
      </div>
    </section>
  );
}

function GovernanceKpi({label,value,tone="normal"}:{label:string;value:number;tone?:"normal"|"warn"|"danger"}){
  return <div className={"regional-kpi "+tone}><span>{label}</span><strong>{value.toLocaleString("ko-KR")}</strong></div>;
}
function Metric({label,value,tone="normal"}:{label:string;value:number|string;tone?:"normal"|"warn"}){
  return <div className={"regional-metric "+tone}><span>{label}</span><strong>{value}</strong></div>;
}
function Line({label,value,attention=false}:{label:string;value:number;attention?:boolean}){
  return <div className="regional-line"><span>{label}</span><strong className={attention?"attention":""}>{value.toLocaleString("ko-KR")}</strong></div>;
}
